import React from "react";
import { Link } from "react-router-dom";
import { formatNZD, timeAgo } from "@/lib/format";
import { buildAdPath } from "@/lib/slugs";
import { LISTING } from "@/constants/testIds";
import FavoriteButton from "@/components/common/FavoriteButton";
import { FileText } from "lucide-react";

const FALLBACK = "https://images.unsplash.com/photo-1607435097405-db48f377bff6?auto=format&fit=crop&w=1200&q=60";

const CAR_SPEC_FIELDS = [
  ["Make", "make"],
  ["Model", "model"],
  ["Year", "year"],
  ["Body Style", "body_style"],
  ["Engine Size", "engine_size"],
  ["Color", "color"],
  ["FuelType", "fuel_type"],
  ["Transmission", "transmission"],
  ["Kilometres", "kilometres"],
];

/**
 * Iter-372 — pull the first upcoming open-home slot off a card. The
 * attribute may arrive as an array of slot strings or one delimited
 * string; render the first entry verbatim (backend orders them).
 */
function firstOpenHomeSlot(listing) {
  const raw = listing?.attributes?.["Open Home Times"];
  if (!raw) return null;
  // Slots arrive as an array or one "·"-delimited string, each slot shaped
  // "July 25, 2026, 1:00 pm → July 25, 2026, 1:30 pm".
  const slots = (Array.isArray(raw) ? raw : String(raw).split(/\s*·\s*|\r?\n|;/))
    .map((s) => String(s).trim())
    .filter(Boolean);
  if (!slots.length) return null;
  const now = Date.now();
  const parsed = slots.map((s) => {
    const [startStr, endStr] = s.split("→").map((t) => t?.trim());
    const start = new Date(startStr);
    return { s, start: Number.isNaN(start.getTime()) ? null : start.getTime(), endStr };
  });
  const pick = parsed.find((p) => p.start && p.start >= now) || parsed[0];
  if (!pick.start) return pick.s; // unparseable — render verbatim
  const d = new Date(pick.start);
  const day = d.toLocaleDateString("en-NZ", { weekday: "short" });
  const time = (x) =>
    x.toLocaleTimeString("en-NZ", { hour: "numeric", minute: "2-digit" }).toLowerCase();
  let range = time(d);
  if (pick.endStr) {
    const e = new Date(pick.endStr);
    if (!Number.isNaN(e.getTime())) range += `–${time(e)}`;
  }
  return `${day} ${range}`;
}

function pickAttr(attrs, keys) {
  if (!attrs) return null;
  for (const k of keys) {
    const camel = k;
    const snake = k.replace(/([A-Z])/g, "_$1").toLowerCase();
    const upper = k.toUpperCase();
    const cap = k.charAt(0).toUpperCase() + k.slice(1);
    for (const variant of [camel, snake, upper, cap, k.replace(/_/g, " ")]) {
      if (attrs[variant] !== undefined && attrs[variant] !== null && attrs[variant] !== "") return attrs[variant];
    }
  }
  return null;
}

function formatDateNZ(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-NZ", { day: "2-digit", month: "long", year: "numeric" });
}

function priceLabel(listing) {
  if (listing.price_type === "negotiable" || listing.price_type === "contact") return "Price Subject to Negotiation";
  if (!listing.price || Number(listing.price) <= 0) return "Price Subject to Negotiation";
  return formatNZD(listing.price);
}

export default function ListingCard({ listing, showOpenHome = false }) {
  // SAFETY-HOLD (iter-224): the shared backend is supposed to filter
  // pending_review / rejected out of public browse — this client-side
  // guard is defence-in-depth so even if a leak ever occurs, buyers
  // never see moderation-in-progress content on the grid.
  const status = String(listing.status || "active").toLowerCase();
  if (status !== "active" && status !== "sold" && status !== "expired") return null;

  // Iter-661 photo policy — prefer the backend-provided `{id}_t.jpg` card
  // thumb when present; fall back to the full first photo. Never build
  // Cloudinary variant URLs client-side.
  const img = listing.thumb || listing.images?.[0] || FALLBACK;
  const to = buildAdPath(listing);
  const isCar = listing.category_slug === "cars";
  // Iter-386 — cars migrated with legacy report PDFs (e.g. Consumer
  // Information Notices) expose `document_names` on the card payload.
  const hasDocs = Array.isArray(listing.document_names) && listing.document_names.length > 0;

  return (
    <article
      data-testid={LISTING.card(listing.id)}
      className="group card-lift relative min-w-0 bg-white rounded-xl shadow-card hover:shadow-card-hover overflow-hidden border border-slate-100"
    >
      <div className="absolute top-3 right-3 z-10" data-testid={LISTING.favouriteBtn(listing.id)}>
        <FavoriteButton listingId={listing.id} />
      </div>
      <Link to={to} data-testid={LISTING.cardLink(listing.id)} className="block" aria-label={listing.title}>
        <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
          <img
            src={img}
            alt={listing.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
            onError={(e) => { e.currentTarget.src = FALLBACK; }}
          />
          {listing.is_featured && <span className="ribbon">Featured</span>}
          {hasDocs && (
            <span
              data-testid={`listing-card-pdf-badge-${listing.id}`}
              title={listing.document_names.join(", ")}
              className="absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-md bg-white/95 backdrop-blur-sm border border-slate-200 shadow-sm text-slate-800 text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5"
            >
              <FileText size={11} className="text-red-600" strokeWidth={2.5} />
              PDF
            </span>
          )}
        </div>

        <div className="p-4 space-y-1.5">
          {/* city (left) + date (right) */}
          {/* Iter-748 §3 — meta row: city truncates, date never wraps */}
          <div className="flex items-center justify-between gap-2 text-[11px] text-slate-500">
            <span className="clamp-1 min-w-0 font-medium">
              {listing.city || listing.region || "New Zealand"}
              {/* Iter-356 — distance chip when sort=distance served it */}
              {listing.distance_km != null && (
                <span className="text-[color:var(--brand-blue)] font-semibold"> · {listing.distance_km} km</span>
              )}
            </span>
            <span className="whitespace-nowrap shrink-0">{formatDateNZ(listing.created_at) || timeAgo(listing.created_at)}</span>
          </div>

          {/* title */}
          <h3 className="font-display font-bold text-slate-900 text-[15px] leading-snug clamp-2 min-h-[2.6rem] group-hover:text-[color:var(--brand-blue)] transition-colors">
            {listing.title}
          </h3>

          {/* price */}
          <div className="pt-1 text-[15px] font-black text-slate-900 tracking-tight">
            {priceLabel(listing)}
          </div>

          {/* address line (uses city, region) */}
          {(listing.area || listing.region) && (
            <div className="text-[11px] text-slate-500 clamp-1">
              {[listing.area, listing.city, listing.region, "New Zealand"].filter(Boolean).join(", ")}
            </div>
          )}

          {/* Iter-372 — first upcoming open-home slot when the filter is on */}
          {showOpenHome && firstOpenHomeSlot(listing) && (
            <div
              className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[11px] font-bold px-2 py-0.5 clamp-1 max-w-full"
              data-testid={`open-home-slot-${listing.id}`}
            >
              Open home {firstOpenHomeSlot(listing)}
            </div>
          )}

          {/* Car spec bullet list */}
          {isCar && listing.attributes && Object.keys(listing.attributes).length > 0 && (
            <ul className="mt-2 pt-2 border-t border-subtle grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-slate-600">
              {CAR_SPEC_FIELDS.map(([label, key]) => {
                const val = pickAttr(listing.attributes, [key, label]);
                if (val === null || val === undefined || val === "") return null;
                return (
                  <li key={label} className="clamp-1">
                    <span className="font-bold text-slate-900">{label}</span> <span>{String(val)}</span>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Posted By chip (teal/green). Iter-274 W6: append a small
              🏢 Business chip when the seller is a verified business. */}
          <div className="pt-2 flex items-center gap-1.5 flex-wrap">
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1 text-[11px] font-semibold clamp-1 max-w-full">
              Posted By : {listing.seller_name || "Seller"}
            </span>
            {listing.seller_is_business && (
              <span
                data-testid={`listing-card-business-chip-${listing.id}`}
                className="inline-flex items-center gap-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest"
                title="Business seller"
              >
                Business
              </span>
            )}
          </div>
        </div>
      </Link>
    </article>
  );
}

export function ListingCardSkeleton() {
  return (
    <div className="bg-white rounded-xl overflow-hidden shadow-card">
      <div className="aspect-[4/3] skeleton" />
      <div className="p-4 space-y-2">
        <div className="skeleton h-3 w-24" />
        <div className="skeleton h-4 w-11/12" />
        <div className="skeleton h-4 w-1/2" />
        <div className="skeleton h-5 w-24 mt-1" />
        <div className="skeleton h-5 w-32 mt-2 rounded-full" />
      </div>
    </div>
  );
}
